import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DocumentMetadata, IDocumentsFilesForm, PageDocument } from '@core';
import { Unsubscriber } from '@core-lib/classes';
import { ApiDocumentTypeName, DocumentOrigin, DocumentType, StatutOperation, TypePieceJointe } from '@core-lib/enums';
import { UserPermission } from '@core-lib/enums/user-permission.enum';
import { markFormGroupTouched } from '@core-lib/functions';
import { IConfirmModalConfig, ICoordonneeBancaire, INatureAffairePieceJointe, IUserLight } from '@core-lib/models';
import { ScannerComponent } from '@core-lib/modules/scanner/components/scanner.component';
import { ConfirmationService } from '@core-lib/services/confirmation-service.service';
import { AlfrescoDocumentStoreActions, AlfrescoDocumentStoreSelectors } from '@core-lib/store/alfresco-documents-store';
import { UserStoreSelectors } from '@core-lib/store/user-store';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { ToastrService } from 'ngx-toastr';
import { ModalRef, ModalService } from '@core-lib/modules/modal-container';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { finalize, map, takeUntil } from 'rxjs/operators';
import { DocumentGedApiService } from '../../services/document-ged-api.service';
import { DocumentGedService } from '../../services/document-ged.service';

@Component({
    selector: 'upload-document-modal',
    templateUrl: './upload-document-modal.component.html',
    styleUrls: ['./upload-document-modal.component.scss'],
    standalone: false
})
export class UploadDocumentModalComponent extends Unsubscriber implements OnInit {
    public isBack: boolean = environment.isBack;
    public isFront: boolean = environment.isFront;

    /**
     * Liste des documents à afficher dans la modale, soit associée à une opération ou à une affaire
     */
    gedDocs: DocumentMetadata[];

    /**
     * Liste des documents de l'affaire, attachés au opérations "EnAttenteDeEnregistrementParLavocat"
     * Cette liste est utilisé pour la vérifcation de fichiers en double
     * US 7365: RG11: le contrôle de doublons ne se fait que sur les documents téléchargés avant enregistrement côté FO. Il ne doit pas s'appliquer sur des documents téléchargé dans des saisies antérieurs.
     */
    affaireGedDocs: DocumentMetadata[];
    documentsCB: DocumentMetadata[];

    /**
     * US 7365: RG6 - RG20
     * Lists des types des piéces jointes, pour allimenter le sélectbox "Type de document"
     * Affiche la liste de tous les types de document possible par ordre alphabétique avec le type "AUTRE" en fin de liste
     * Par défaut, la valeur est "Sélectionnez"
     * NB: POUR LA POPIN DE TELECHARGEMENT OBLIGATOIRE: Affiche QUE la liste des pièces jointes obligatoires paramétrées pour chaque nature d'affaire (paramétrage existant)
     */
    typesPieceJointeAll: INatureAffairePieceJointe[] = [];
    typesPieceJointe$: Observable<INatureAffairePieceJointe[]>;

    isLoadingFiles: boolean = false;
    files: any[] = [];

    _compteId: string;
    _pageAffaireId: string;
    _operationId: string;
    _structureId: string;
    _signatureId: string;
    _evenementId: string;
    _courrierId: string;
    _delegationBancaireId: string;
    _instructionPermanenteId: string;
    
    _form: UntypedFormGroup;
    _isAddModalWithoutType: boolean;
    _courrierFile: File;
    _coordonneeBancaire: ICoordonneeBancaire;
    _lotId: string;

    // pagination
    pageSizes = [25, 50, 100];
    currentPage = 1;
    currentPageSize = this.pageSizes[0];
    hasMoreItems: boolean;
    skipCount: number;
    isAsc: boolean = false;
    // Tri par défaut aligné sur le défaut serveur (DateCreation desc) : l'indicateur de colonne reflète l'ordre réel.
    lastSortColomn: string = 'createdAt';
    loading: boolean = false;
    _documentType: string = "";

    @Input() filesToDeleteIds: string[] = [];
    @Input() tempFilesToDeleteIds: string[] = [];
    @Input() isFromPageSaisie: boolean = true;
    @Input() statutOperation: string;
    @Input() numeroCompteBancaire: string;
    @Input() forReUse: boolean = false; //* pour reutiliser la modal sans les boutons d'actions, titre etc..
    @Input() scannerFileName: string;
    @Input() isFromFrontContactPage: boolean = false;
    @Input() isFromPageCB: boolean = false;
    @Input() isConsultMode: boolean = false;
    // Bug 19012: la suppression d'une PJ deja enregistree est seulement marquee (filesToDeleteIds) ;
    // le parent la realise a l'enregistrement, fermer la popin sans enregistrer la conserve.
    @Input() deferAlfrescoDelete: boolean = false;
    @Input() set lotId(value: string) {
        if (value) {
            this.isLoadingFiles = true;
            this._lotId = value;
            this.documentGedApiService
                .getLotDocuments(value)
                .pipe(takeUntil(this.destroySubscriptions$))
                .subscribe(res => {
                    this.gedDocs = res?.filter(x => this.filesToDeleteIds.indexOf(x?.id) === -1)
                        .sort((a, b) => this.compare(a?.createdAt, b.createdAt, false));
                    this.affaireGedDocs = res?.filter(x => this.filesToDeleteIds.indexOf(x.id) === -1);
                    this.isLoadingFiles = false;
                });
        }
    }

    get lotId(): string {
        return this._lotId;
    }
    @Input() set documentType(value: string) {
        if (value) {
            this._documentType = value;
        }
    };

    get documentType(): string {
        return this._documentType;
    }

    @Input() set coordonneesBancairesFilesFromAlfresco(value: DocumentMetadata[]) {
        if (value) {
            this.gedDocs = value.filter(x => this.filesToDeleteIds.indexOf(x.id) === -1)
                .sort((a, b) => this.compare(a?.createdAt, b.createdAt, false));
            this.affaireGedDocs = value.filter(x => this.filesToDeleteIds.indexOf(x?.id) === -1);
        }
    }

    @Input() set courrierFile(value: File) {
        if (value) {
            this._courrierFile = value;
            this.addFileForm(this.courrierFile);
        }
    }

    get courrierFile(): File {
        return this._courrierFile;
    }

    @Input() set isAddModalWithoutType(value: boolean) {
        if (value) {
            this._isAddModalWithoutType = value;
        } else {
            this._isAddModalWithoutType = false;
        }
    }

    get isAddModalWithoutType(): boolean {
        return this._isAddModalWithoutType;
    }

    @ViewChild('fileDropRef', { static: false }) fileDropEl: ElementRef;

    currentUser$ = this.store$.select(UserStoreSelectors.selectUser);
    private currentUser: IUserLight;

    @Input() set form(value: UntypedFormGroup) {
        if (value) {
            this._form = value;
            this.putFormArrayInOtherFormArray(<UntypedFormArray>value?.get('filesList'), this.filesListTemps);
        }
    }

    get form(): UntypedFormGroup {
        return this._form;
    }
    get filesList(): UntypedFormArray {
        return <UntypedFormArray>this.form?.get('filesList');
    }

    // Un formulaire qui contient la liste des fichiers qu'on vient d'uploader, c'est une variable créer pour contourner le fait d'annuler une édition
    formTemps: UntypedFormGroup;
    get filesListTemps(): UntypedFormArray {
        return <UntypedFormArray>this.formTemps?.get('filesList');
    }

    @Input() set structureId(value: string) {
        if (value) {
            this._structureId = value;
        }
    }

    get structureId(): string {
        return this._structureId;
    }

    @Input() set signatureId(value: string) {
        if (value) {
            this._signatureId = value;
        }
    }

    get signatureId(): string {
        return this._signatureId;
    }

    @Input() set coordonnesBancaires(value: ICoordonneeBancaire) {
        if (value && value?.id) {
            this._coordonneeBancaire = value;
            this.store$.dispatch(AlfrescoDocumentStoreActions.LoadCoordonneesBancairesDocsRequest(value?.retraitId, value?.id));
        }
    }

    get coordonnesBancaires(): ICoordonneeBancaire {
        return this._coordonneeBancaire;
    }

    @Input() set operationId(value: string) {
        if (value) {
            this.isLoadingFiles = true;
            this._operationId = value;
            // recuperer les ficheirs de l'operation à partir du alfresco
            this.documentGedApiService
                .getOperationDocuments(value, this.currentPageSize, this.currentPage)
                .pipe(takeUntil(this.destroySubscriptions$))
                .subscribe(res => {
                    this.hasMoreItems = res.hasMoreItems;
                    this.skipCount = res.skipCount;
                    this.gedDocs = res?.documents?.filter(x => this.filesToDeleteIds.indexOf(x?.id) === -1)
                        .sort((a, b) => this.compare(a?.createdAt, b.createdAt, false));
                    this.affaireGedDocs = res?.documents?.filter(x => this.filesToDeleteIds.indexOf(x.id) === -1);
                    this.isLoadingFiles = false;
                });
        }
    }

    get operationId(): string {
        return this._operationId;
    }

    @Input() set instructionPermanenteId(value: string) {
        if (value) {
            this.isLoadingFiles = true;
            this._instructionPermanenteId = value;
            // recuperer les ficheirs de l'instruction permanente à partir du alfresco
            this.documentGedApiService
                .getInstructionPermanenteDocuments(value)
                .pipe(takeUntil(this.destroySubscriptions$))
                .subscribe(res => {
                    this.gedDocs = res?.filter(x => this.filesToDeleteIds.indexOf(x?.id) === -1)
                        .sort((a, b) => this.compare(a?.createdAt, b.createdAt, false));
                    this.affaireGedDocs = res?.filter(x => this.filesToDeleteIds.indexOf(x.id) === -1);
                    this.isLoadingFiles = false;
                });
        }
    }

    get instructionPermanenteId(): string {
        return this._instructionPermanenteId;
    }

    @Input() set compteId(value: string) {
        this.isLoadingFiles = true;
        this._compteId = value;
        if (value && !this.structureId) {
            // recuperer les ficheirs du compte à partir du alfresco
            this.documentGedApiService
                .getCompteDocuments(value, this.currentPageSize, this.currentPage)
                .pipe(takeUntil(this.destroySubscriptions$))
                .subscribe(res => {
                    this.hasMoreItems = res.hasMoreItems;
                    this.skipCount = res.skipCount;
                    this.gedDocs = res?.documents?.filter(x => this.filesToDeleteIds.indexOf(x?.id) === -1)
                        .sort((a, b) => this.compare(a?.createdAt, b.createdAt, false));
                    this.affaireGedDocs = res?.documents?.filter(x => this.filesToDeleteIds.indexOf(x.id) === -1);
                    if (this.isConsultDocMode && this.isBack && this.gedDocs?.length == 1) {
                        this.getUrlFile(this.gedDocs[0]);
                    }

                    this.isLoadingFiles = false;
                });
        }
        if (value && this.structureId && this.signatureId) {
            // recuperer les ficheirs de la signature à partir du alfresco
            this.documentGedApiService
                .getRecueilSignatureDocuments(this.signatureId, this.currentPageSize, this.currentPage)
                .pipe(takeUntil(this.destroySubscriptions$))
                .subscribe(res => {
                    this.hasMoreItems = res.hasMoreItems;
                    this.skipCount = res.skipCount;
                    this.gedDocs = res?.documents?.filter(x => this.filesToDeleteIds.indexOf(x?.id) === -1)
                        .sort((a, b) => this.compare(a?.createdAt, b.createdAt, false));
                    this.affaireGedDocs = res?.documents?.filter(x => this.filesToDeleteIds.indexOf(x.id) === -1);
                    if (this.isConsultDocMode && this.isBack && this.gedDocs?.length == 1) {
                        this.getUrlFile(this.gedDocs[0]);
                    }
                    this.isLoadingFiles = false;
                });
        }
    }

    get compteId(): string {
        return this._compteId;
    }

    @Input() set evenementId(value: string) {
        this._evenementId = value;
        if (value) {
            this.isLoadingFiles = true;
            // recuperer les ficheirs des evenements à partir d'alfresco
            this.documentGedApiService
                .getEventDocuments(value, this.currentPageSize, this.currentPage)
                .pipe(takeUntil(this.destroySubscriptions$))
                .subscribe(res => {
                    this.hasMoreItems = res.hasMoreItems;
                    this.skipCount = res.skipCount;
                    this.gedDocs = res?.documents?.filter(x => this.filesToDeleteIds.indexOf(x.id) === -1)
                        .sort((a, b) => this.compare(a?.createdAt, b.createdAt, false));
                    this.affaireGedDocs = res?.documents?.filter(x => this.filesToDeleteIds.indexOf(x.id) === -1);
                    this.isLoadingFiles = false;
                });
        }
    }

    get delegationBancaireId(): string {
        return this._delegationBancaireId;
    }

    @Input() set delegationBancaireId(value: string) {
        this._delegationBancaireId = value;
        if (value) {
            this.isLoadingFiles = true;
            // recuperer les ficheirs des delegationBancaires à partir d'alfresco
            this.documentGedApiService
                .getDelegationBancaireDocuments(value, this.currentPageSize, this.currentPage)
                .pipe(takeUntil(this.destroySubscriptions$))
                .subscribe(res => {
                    this.hasMoreItems = res.hasMoreItems;
                    this.skipCount = res.skipCount;
                    this.gedDocs = res?.documents?.filter(x => this.filesToDeleteIds.indexOf(x.id) === -1)
                        .sort((a, b) => this.compare(a?.createdAt, b.createdAt, false));
                    this.affaireGedDocs = res?.documents?.filter(x => this.filesToDeleteIds.indexOf(x.id) === -1);
                    this.isLoadingFiles = false;
                });
        }
    }

    get evenementId(): string {
        return this._evenementId;
    }

    @Input() set courrierId(value: string) {
        this._courrierId = value;
        if (value) {
            this.isLoadingFiles = true;
            // recuperer les ficheirs des evenements à partir d'alfresco
            this.documentGedApiService
                .getCourrierDocuments(value, this.currentPageSize, this.currentPage)
                .pipe(takeUntil(this.destroySubscriptions$))
                .subscribe(res => {
                    this.hasMoreItems = res.hasMoreItems;
                    this.skipCount = res.skipCount;
                    this.gedDocs = res?.documents?.filter(x => this.filesToDeleteIds.indexOf(x.id) === -1)
                        .sort((a, b) => this.compare(a?.createdAt, b.createdAt, false));
                    this.affaireGedDocs = res?.documents?.filter(x => this.filesToDeleteIds.indexOf(x.id) === -1);
                    this.isLoadingFiles = false;
                });
        }
    }

    // TODO: Setter not working
    setCourrierId(value: string) {
        this._courrierId = value;
        if (value) {
            this.isLoadingFiles = true;
            // recuperer les ficheirs des evenements à partir d'alfresco
            this.documentGedApiService
                .getCourrierDocuments(value, this.currentPageSize, this.currentPage)
                .pipe(takeUntil(this.destroySubscriptions$))
                .subscribe(res => {
                    this.hasMoreItems = res.hasMoreItems;
                    this.skipCount = res.skipCount;
                    this.gedDocs = res?.documents?.filter(x => this.filesToDeleteIds.indexOf(x.id) === -1)
                        .sort((a, b) => this.compare(a?.createdAt, b.createdAt, false));
                    this.affaireGedDocs = res?.documents?.filter(x => this.filesToDeleteIds.indexOf(x.id) === -1);
                    this.isLoadingFiles = false;
                });
        }
    }

    get courrierId(): string {
        return this._courrierId;
    }

    @Input() set affaireId(value: string) {
        if (value) {
            this.isLoadingFiles = true;
            // recuperer les ficheirs de l'affaire à partir du alfresco
            this.documentGedApiService
                .getAffaireDocumentsByType(value, DocumentOrigin.avocat, this.currentPageSize, this.currentPage)
                .pipe(takeUntil(this.destroySubscriptions$))
                .subscribe(res => {
                    this.hasMoreItems = res.hasMoreItems;
                    this.skipCount = res.skipCount;
                    this.gedDocs = res?.documents?.filter(x => this.filesToDeleteIds.indexOf(x.id) === -1)
                        .sort((a, b) => this.compare(a?.createdAt, b.createdAt, false));
                    this.affaireGedDocs = res?.documents?.filter(x => this.filesToDeleteIds.indexOf(x.id) === -1);
                    this.isLoadingFiles = false;
                });
        }
    }

    @Input() set pageAffaireId(value: string) {
        if (value) {
            this.isLoadingFiles = true;
            this._pageAffaireId = value;
            this.documentGedApiService
                .getAffaireDocuments(value, this.currentPageSize, this.currentPage)
                .pipe(takeUntil(this.destroySubscriptions$))
                .subscribe(res => {
                    this.hasMoreItems = res.hasMoreItems;
                    this.skipCount = res.skipCount;
                    this.gedDocs = res?.documents?.filter(x => this.filesToDeleteIds.indexOf(x.id) === -1)
                        .sort((a, b) => this.compare(a?.createdAt, b.createdAt, false));
                    this.affaireGedDocs = res?.documents?.filter(x => this.filesToDeleteIds.indexOf(x.id) === -1);
                    this.isLoadingFiles = false;
                });
        }
    }

    get pageAffaireId(): string {
        return this._pageAffaireId;
    }

    @Input() set affaireSaisieId(value: string) {
        if (value) {
            // recuperer les ficheirs de l'affaire à partir du alfresco (Fichier pour les opérations qui sont en cours de saisie par l'avocat)
            this.isFromPageSaisie
                ? this.documentGedApiService
                    .getAffaireDocumentsByType(value, DocumentOrigin.avocat, this.currentPageSize, this.currentPage)
                    .pipe(takeUntil(this.destroySubscriptions$))
                    .subscribe(res => {
                        this.hasMoreItems = res.hasMoreItems;
                        this.skipCount = res.skipCount;
                        this.affaireGedDocs = res?.documents?.filter(x => this.filesToDeleteIds.indexOf(x?.id) === -1);
                    })
                : this.documentGedApiService
                    .getAffaireDocumentsByType(value, DocumentOrigin.carpa, this.currentPageSize, this.currentPage)
                    .pipe(takeUntil(this.destroySubscriptions$))
                    .subscribe(res => {
                        this.affaireGedDocs = res?.documents?.filter(x => this.filesToDeleteIds.indexOf(x?.id) === -1);
                    });

            // User Story 14258: Modification du comportement de la PJ obligatoire
            // Dans la page saisie coté FO, on filtre les types de piéce jointe par nature d'affaire
            if (this.isModalForRequiredDocs) {
                this.typesPieceJointe$ = this.documentGedApiService.getTypesPieceJointeByAffaire(value).pipe(
                    takeUntil(this.destroySubscriptions$),
                    map(pja => pja.filter(pj => !pj.isCodeTechnique))
                );
            }
        }
    }

    @Input() set natureAffaireId(value: string) {
        if (value) {
            // User Story 14258: Modification du comportement de la PJ obligatoire
            // Dans la page saisie coté FO, on filtre les types de piéce jointe par nature d'affaire
            if (this.isFront) {
                this.typesPieceJointe$ = this.documentGedApiService.getTypesPieceJointeByNature(value).pipe(
                    takeUntil(this.destroySubscriptions$),
                    map(pja => pja.filter(pj => !pj.isCodeTechnique))
                );
            }
        }
    }

    @Input()
    set isConsultDocMode(value: boolean) {
        if (value) {
            if (this.gedDocs?.length == 1 && this.isBack) {
                this.getUrlFile(this.gedDocs[0]);
            }
            this._isConsultDocMode = value;
        }
    }
    private _isConsultDocMode: boolean = false;
    get isConsultDocMode(): boolean {
        return this._isConsultDocMode;
    }

    @Input() isModalForRequiredDocs: boolean = false;
    @Input()
    set setDefaultType(value: boolean) {
        this._setDefaultType = value;
    }
    private _setDefaultType: boolean = false;
    get setDefaultType(): boolean {
        return this._setDefaultType;
    }
    /**
     * Liste des fichiers qui sont attachés aux coordonnées bancaires
     */
    // @Input() coordonneesBancairesFiles: File[];
    @Input() coordonneesBancairesFiles: {
        file: File;
        commentaire: string | null;
        libelle: string;
        typePieceJointeId: string;
        typePieceJointe: string;
    }[] = [];

    @Output() reload: EventEmitter<boolean> = new EventEmitter();
    @Output() delete: EventEmitter<string> = new EventEmitter();
    @Output() public fileDropped: EventEmitter<any> = new EventEmitter(); // Form
    /**
     * Observable scan document
     */
    documents$ = this.documentGedService.documents$;
    alfrescoCBDocuments$ = this.store$.select(AlfrescoDocumentStoreSelectors.selectCoordonnesBancairesDocs);

    // Filter available types of documents
    private categoryPJFilter$ = new BehaviorSubject<string>('divers');
    private _categoryPJFilter: string;

    @Input() set categoryPJFilter(value: string | null) {
        this.categoryPJFilter$.next(value);
        this._categoryPJFilter = value;
    }

    get categoryPJFilter(): string {
        return this._categoryPJFilter;
    }


    @ViewChild('uploadDocFooterPageAnchor') footerAnchor: ElementRef;

    /**
     * Constructeur
     * @param modalRef La référence de l'objet modal
     * @param fb Angular form builder
     */
    constructor(
        public modalRef: ModalRef,
        private fb: UntypedFormBuilder,
        private documentGedService: DocumentGedService,
        private documentGedApiService: DocumentGedApiService,
        private modalService: ModalService,
        private translateService: TranslateService,
        protected store$: Store,
        private confirmationService: ConfirmationService,
        private toastr: ToastrService
    ) {
        super();
    }

    ngOnInit(): void {
        this.currentUser$.subscribe(res => {
            this.currentUser = res;
        });

        this.formTemps = this.fb.group({
            filesList: new UntypedFormArray([])
        });

        this.typesPieceJointe$ = combineLatest([
            this.documentGedApiService.typesPieceJointe$,
            this.categoryPJFilter$
        ]).pipe(
            takeUntil(this.destroySubscriptions$),
            map(([pja, filter]) =>
                pja
                    .filter(pj => !pj.isCodeTechnique)
                    .filter(pj =>
                        !filter || filter.length === 0 || filter.includes(pj.categorie) // adjust 'code' to match your data model
                    )
            )
        );

        this.documentGedApiService.typesPieceJointe$.pipe(takeUntil(this.destroySubscriptions$)).subscribe(res => {
            this.typesPieceJointeAll = res.filter(pj => !pj.isCodeTechnique);
        });

        this.documentGedService.documents$.pipe(takeUntil(this.destroySubscriptions$)).subscribe(doc => {
            if (doc != null && doc.docs != null) {
                const myBlob = new Blob(doc.docs, { type: 'application/pdf' });
                // TODO: filename shoud be changed accordingly the business rules.
                // Here the filename is used for demo propose
                const tempFilename = doc.filename;
                const myFile = this.blobToFile(myBlob, tempFilename);
                if (myFile.size > 1) {
                    this.addFileForm(myFile);
                    this.documentGedService.resetDocuments();

                    // Page Anchor
                    this.scrollToElement(this.footerAnchor);
                }
            }
        });

        this.alfrescoCBDocuments$.subscribe(docs => {
            this.documentsCB = docs;
        });
    }

    scrollToElement($element): void {
        if ($element && $element.nativeElement) {
            $element.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        }
    }

    blobToFile(blob: Blob, fileName: string): File {
        return new File([blob], fileName, { type: blob.type, lastModified: new Date().getTime() });
    }

    /**
     * Action fermeture popup: ANNULATION
     */
    onClose(): void {
        this.modalRef.hide();
        this.reload.emit(true);
    }

    /**
     * Action Enregister de la modal Coordonnées Bancaires
     */
    onConfirmFromCB(): boolean {
        markFormGroupTouched(this.formTemps as UntypedFormGroup);
        const formArray = this.formTemps?.get('filesList') as UntypedFormArray;


        let totalFileSize = 0;
        if (formArray?.length > 0) {
            formArray.controls.forEach(ctrl => {
                const f = ctrl.value?.file;
                if (f) totalFileSize += f.size;
            });
        }
        totalFileSize = totalFileSize / (1024 * 1024);


        const hasNewFiles = (formArray?.length ?? 0) > 0;
        const hasTempCbFiles = (this.coordonneesBancairesFiles?.length ?? 0) > 0;
        const hasAlfrescoCbDocs = (this.documentsCB?.length ?? 0) > 0;


        if (totalFileSize === 0 && !hasTempCbFiles && !hasAlfrescoCbDocs) {
            this.showModal('Documents.Form.TooSmallFile');
            return false;
        }

        if ((this.formTemps.valid && hasNewFiles) || hasTempCbFiles) {
            markFormGroupTouched(this.formTemps as UntypedFormGroup);


            const base = formArray?.value ?? [];
            const merged = [...base, ...(this.coordonneesBancairesFiles ?? [])];


            const pjId = this.typesPieceJointeAll.find(pj => pj.code === TypePieceJointe.RIB)?.id;
            const forms = (merged ?? []).map(x => ({ ...x, typePieceJointeId: pjId }));

            this.fileDropped.emit(forms);


            formArray.clear();
            return true;
        } else {
            markFormGroupTouched(this.formTemps as UntypedFormGroup);
            return false;
        }
    }


    /**
     * Action confirmation d'ajout fichier(s)
     */
    onConfirm(): void {
        markFormGroupTouched(this.formTemps as UntypedFormGroup);
        const formValue = this.formTemps.get('filesList') as UntypedFormArray;

        if (this.isModalForRequiredDocs && formValue?.length === 0) {
            return;
        }

        // Calculate total size of files in MB
        let totalFileSize = 0;
        if (formValue.length > 0) {
            formValue.controls.forEach(control => {
                const file = control.value;
                if (file) {
                    totalFileSize += file.file.size; // File size in bytes
                }
            });
            totalFileSize = totalFileSize / (1024 * 1024); // Convert bytes to megabytes (MB)
        }

        // Check if total file size exceeds 25 MB
        if (totalFileSize > 25) {
            this.showModal('Documents.Form.TooLargeFiles');
            return;
        }

        if (this.formTemps.valid) {
            if (this.isFromPageSaisie) {
                markFormGroupTouched(this.form as UntypedFormGroup);
                // Delete all selected files informations
                this.filesList?.clear();
                this.putFormArrayInOtherFormArray(this.filesListTemps, this.filesList);
                // Invoke file transfer event
                if (this.tempFilesToDeleteIds.length > 0) {
                    this.documentGedService.sendFilesToDeleteIds(this.tempFilesToDeleteIds);
                }
                this.emitData(this.formTemps);
                return;
            } else {
                this.loading = true;
                if (formValue.length > 0) {
                    let filesForm = {} as IDocumentsFilesForm;
                    filesForm.documents = { filesList: formValue?.value };
                    if (this.operationId) {
                        filesForm.operationId = this.operationId;
                        this.documentGedApiService
                            .uploadOperationFile(filesForm)
                            .pipe(finalize(() => (this.loading = false)))
                            .subscribe({
                                next: () => {
                                    this.modalRef.hide();
                                    this.reload.emit(true);
                                },
                                error: error => { }
                            });
                    }

                    // Upload lot documents
                    if (this.lotId) {
                        filesForm.lotId = this.lotId;
                        this.documentGedApiService
                            .uploadLotFile(filesForm)
                            .pipe(finalize(() => (this.loading = false)))
                            .subscribe({
                                next: () => {
                                    this.modalRef.hide();
                                    this.reload.emit(true);
                                },
                                error: error => { }
                            });
                    }
                    // Upload event documents
                    if (this.evenementId) {
                        filesForm.evenementId = this.evenementId;
                        this.documentGedApiService
                            .uploadEvenementFile(filesForm)
                            .pipe(finalize(() => (this.loading = false)))
                            .subscribe({
                                next: () => {
                                    this.modalRef.hide();
                                    this.reload.emit(true);
                                },
                                error: error => { }
                            });
                    }

                    // Upload delegationf bancaires documents
                    if (this.delegationBancaireId) {
                        filesForm.delegationBancaireId = this.delegationBancaireId;
                        this.documentGedApiService
                            .uploadDelegationBancaireFile(filesForm)
                            .pipe(finalize(() => (this.loading = false)))
                            .subscribe({
                                next: () => {
                                    this.modalRef.hide();
                                    this.reload.emit(true);
                                },
                                error: error => { }
                            });
                    }

                    // Upload courrier documents
                    if (this.courrierId) {
                        filesForm.courrierId = this.courrierId;
                        this.documentGedApiService
                            .uploadCourrierFile(filesForm)
                            .pipe(finalize(() => (this.loading = false)))
                            .subscribe({
                                next: () => {
                                    this.modalRef.hide();
                                    this.reload.emit(true);
                                },
                                error: error => { }
                            });
                    }

                    // Upload documents directly to account
                    if (this.compteId && !this.structureId) {
                        filesForm.compteId = this.compteId;
                        this.documentGedApiService
                            .uploadCompteFile(filesForm)
                            .pipe(finalize(() => (this.loading = false)))
                            .subscribe({
                                next: () => {
                                    this.modalRef.hide();
                                    this.reload.emit(true);
                                },
                                error: error => { }
                            });
                    }

                    // Upload documents directly to IP
                    if (this.instructionPermanenteId) {
                        filesForm.instructionPermanenteId = this.instructionPermanenteId;
                        this.documentGedApiService
                            .uploadInstructionPermanenteFile(filesForm)
                            .pipe(finalize(() => (this.loading = false)))
                            .subscribe({
                                next: () => {
                                    this.modalRef.hide();
                                    this.reload.emit(true);
                                },
                                error: error => { }
                            });
                    }
                    // Upload signature documents
                    if (this.compteId && this.signatureId) {
                        this.documentGedApiService
                            .uploadRecueilSignatureFile(filesForm, this.signatureId, this.compteId)
                            .pipe(finalize(() => (this.loading = false)))
                            .subscribe({
                                next: () => {
                                    this.modalRef.hide();
                                    this.reload.emit(true);
                                },
                                error: error => { }
                            });
                    }
                    // Upload documents directly to affaire
                    if (this.pageAffaireId) {
                        filesForm.affaireId = this.pageAffaireId;
                        this.documentGedApiService
                            .uploadAffaireFile(filesForm)
                            .pipe(finalize(() => (this.loading = false)))
                            .subscribe({
                                next: () => {
                                    this.modalRef.hide();
                                    this.reload.emit(true);
                                },
                                error: error => { }
                            });
                    }
                    // Upload documents to carpa
                    if (this.categoryPJFilter == 'carpa') {
                        this.documentGedApiService
                            .uploadCarpaFile(filesForm)
                            .pipe(finalize(() => (this.loading = false)))
                            .subscribe({
                                next: () => {
                                    this.modalRef.hide();
                                    this.reload.emit(true);
                                },
                                error: error => { }
                            });
                    }
                } else {
                    this.modalRef.hide();
                    this.reload.emit(true);
                }
            }
        }
    }

    /**
     * Transférer les fichiers au parent dans un objet "data"
     * @param data
     */
    private emitData(data: UntypedFormGroup): void {
        this.documentGedService.sendDataForm(data);
        this.reload.emit(true);
        this.modalRef.hide();
    }

    bindFilesWithForm(files: any[]): void {
        for (const f of files) {
            this.addFileForm(f);
        }
    }

    addFileForm(file: File): void {
        if (!this.filesListTemps.value) {
            this.formTemps = this.fb.group({
                filesList: this.fb.array([this.createFileForm(file)])
            });
        } else {
            this.filesListTemps.push(this.createFileForm(file));
        }
    }

    createFileForm(file: File): UntypedFormGroup {
        let formGroup: UntypedFormGroup;

        if (this.isAddModalWithoutType) {
            formGroup = this.fb.group({
                file: [file, [Validators.required]],
                libelle: [null],
                commentaire: [null]
            });
        } else if (this.isFromPageCB) {
            formGroup = this.fb.group({
                file: [file, [Validators.required]],
                typePieceJointeId: [
                    { value: this.typesPieceJointeAll.find(x => x.code === TypePieceJointe.RIB)?.id, disabled: true },
                    [Validators.required]
                ],
                libelle: [null],
                commentaire: [null]
            });
        } else {
            let defaultTypeId = this.setDefaultType
                ? this.delegationBancaireId
                    ? this.typesPieceJointeAll.find(x => x.code === TypePieceJointe.Autre)?.id
                    : this.typesPieceJointeAll.find(x => x.code === TypePieceJointe.Autorisation)?.id
                : null;

            let defaultComment = this.delegationBancaireId
                ? this.translateService.instant('Documents.Form.CommentDelegationBancaire', { numeroCompte: this.numeroCompteBancaire })
                : null;

            formGroup = this.fb.group({
                file: [file, [Validators.required]],
                typePieceJointeId: [defaultTypeId, [Validators.required]],
                libelle: [null],
                commentaire: defaultComment
            });
        }

        return formGroup;
    }

    removeFile(index: number, name: string): void {
        this.files = this.files.filter(x => x.name != name);
        this.filesListTemps?.removeAt(index);

        if (this.isFromPageCB) {
            this.coordonneesBancairesFiles = this.coordonneesBancairesFiles.filter(cb => cb.file.name !== name);
            this.fileDropped.emit(this.coordonneesBancairesFiles);
        }
    }
    /**
     *
     * @deprecated ticket 13996, demande de suppression direct du doc depuis la popIn, utiliser removeAlfrescoFileById()
     */
    removeAlfrescoFile(id: string): void {
        if (this.isFromPageSaisie) {
            this.tempFilesToDeleteIds.push(id);
        } else {
            this.documentGedApiService.deleteDocumentsById(id, this._documentType).subscribe({
                next: () => { },
                error: error => { }
            });
        }
    }

    removeAlfrescoFileById(id: string): void {
        if (id !== '') {
            this.documentGedApiService.deleteDocumentsById(id, this.documentType).subscribe({
                next: () => {
                    this.delete.emit(id);
                    this.affaireGedDocs = this.affaireGedDocs.filter(doc => doc.id !== id);
                    this.reload.emit(true);
                },
                error: error => { }
            });
        }
    }

    formatBytes(bytes, decimals = 1): string {
        return this.documentGedService.formatBytes(bytes, decimals);
    }

    /**
     * Colonne « attaché à » des pièces jointes d'affaire : le rattachement est porté par le
     * documentType du document. Hors Affaire, la recherche d'une affaire ne remonte que des documents
     * issus de ses opérations (opération, commentaire de traitement, coordonnées bancaires de retrait).
     */
    getAttacheA(item: { documentType?: DocumentType }): string {
        return item?.documentType && item.documentType !== DocumentType.Affaire ? 'Opération' : 'Affaire';
    }

    DownloadDocument(item: DocumentMetadata): void {
        // Le type porté par le document prime (comme pour le viewer) ; l'@Input documentType ne sert que de repli
        // quand l'appelant de la popin l'a renseigné mais que le document ne le porte pas.
        const documentType = item.documentType ? ApiDocumentTypeName[item.documentType] : this.documentType;
        this.documentGedApiService.downloadDocument(item.id, item.name, documentType);
    }

    onRemoveFile(index: number, id: string): void {
        let selectedFile = this.gedDocs?.find(x => x?.id === id)?.name;
        const config: IConfirmModalConfig = {
            textsKey: 'ConfirmDeleteFile',
            bodyParams: { selectedFile: selectedFile },
            modalOptions: {
                class: 'modal-md'
            }
        };
        this.confirmationService.openConfirmationModal(config, () => {
            this.gedDocs?.splice(index, 1);
            if (this.deferAlfrescoDelete) {
                this.filesToDeleteIds = [...this.filesToDeleteIds, id];
                this.affaireGedDocs = this.affaireGedDocs?.filter(doc => doc.id !== id);
                this.delete.emit(id);
            } else if (!this.forReUse || !this.isFromFrontContactPage) {
                this.removeAlfrescoFileById(id);
            }
        });
    }

    onDownload(item, name) {
        saveAs(item, name);
    }

    openDeleteModal(textsKey: string, bodyParams?: any) {
        const config: IConfirmModalConfig = {
            textsKey,
            bodyParams,
            modalOptions: {
                class: 'modal-md'
            }
        };
        this.confirmationService.openConfirmationModal(config);
    }

    canDeleteFile(item: DocumentMetadata): boolean {
        // Vérification des permissions principales
        const { permissions, id } = this.currentUser;

        const isAvocat = permissions.includes(UserPermission.IsAvocat);
        const canDeleteDocs = permissions.includes(UserPermission.DocumentCanDelete);
        const isAdminEmdf = permissions.includes(UserPermission.IsAdminEmdf);
        const isCreator = id === item.utilisateurCreationId;

        if (!(isAvocat || canDeleteDocs)) return false;

        // Si un doc a été ajouté sur le FO par l'avocat (le document contient un CNBF avocat) : aucun user BO (même un admin carpa) ne peut les supprimer
        if (!isAvocat && item.cnbf) return false;

        if (this.isConsultDocMode && this.isFront) return false;

        if (isAdminEmdf) return true;

        // Vérification du type de pièce jointe pour un avocat
        if (isAvocat && item.typePieceJointe !== TypePieceJointe.AutorisationPrelevementHonoraire) {
            return true;
        }

        // Vérifications supplémentaires liées aux documents et opérations
        if (this.courrierId || (this.evenementId && !this.isConsultDocMode)) return true;
        if (this.documentsCB?.length && !this.documentsCB.some(x => x.id === item.id)) return true;

        // Vérification dans le cadre d'une opération
        if (this.operationId) {
            if (!this.documentsCB?.length && isCreator) return true;
            if (this.statutOperation === StatutOperation.EnAttenteEnregistrementParAvocat && isCreator) return true;
            if (this.statutOperation !== StatutOperation.EnAttenteEnregistrementParAvocat && isAdminEmdf) return true;
            if (this.statutOperation === StatutOperation.EnAttenteEnregistrementParCarpa && isCreator) return true;
        }
        if (this.lotId) {
            return permissions.includes(UserPermission.ManageLotDocuement);
        }
        return false;
    }

    deleteTooltip(item: DocumentMetadata): string {
        return this.canDeleteFile(item) ? '' : this.translateService.instant('Documents.Form.NotAuthorizedDeleteFile');
    }

    pJTooltip(name: string): string {
        return this.translateService.instant(name);
    }

    getNomTypePieceJointe(typePieceJointeId: string): string {
        return this.typesPieceJointeAll.find(x => x.id === typePieceJointeId)?.nom || '';
    }

    getUrlFile(item: DocumentMetadata): void {
        const viewerTab = window.open('', '_blank');
        if (viewerTab) {
            const loadingMessage = this.translateService.instant('Documents.Viewer.Loading');
            viewerTab.document.write(
                `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8"><title>${loadingMessage}</title></head>` +
                    `<body style="margin:0;display:flex;align-items:center;justify-content:center;height:100vh;font-family:Arial,Helvetica,sans-serif;color:#333;font-size:1.1rem;">` +
                    `<p>${loadingMessage}</p></body></html>`
            );
            viewerTab.document.close();
        }
        const documentType = ApiDocumentTypeName[item.documentType];
        this.documentGedApiService
            .getDocumentViewerUrl(item.id, documentType)
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe({
                next: res => {
                    if (viewerTab) {
                        viewerTab.opener = null;
                        viewerTab.location.href = res.urlViewer;
                    } else {
                        window.open(res.urlViewer, '_blank', 'noopener');
                    }
                },
                error: (err: Error) => {
                    viewerTab?.close();
                    this.toastr.error(err.message);
                }
            });
    }

    getSortClasses(colName: string): object {
        const isSameCol = colName === this.lastSortColomn;
        return {
            'col-sorted-asc': isSameCol && this.isAsc,
            'col-sorted-desc': isSameCol && !this.isAsc
        };
    }

    sortTable(colName: string): void {
        const isSameCol = colName === this.lastSortColomn;
        this.lastSortColomn = colName;
        this.isAsc = isSameCol ? !this.isAsc : true;
        // Le tri est délégué à l'API : on revient en page 1 et on recharge avec le nouveau tri.
        this.currentPage = 1;
        this.loadDocuments();
    }

    /**
     * Construit le paramètre de tri attendu par l'API (`<PropertyName> asc|desc`).
     * Le mapping colonne UI -> propriété back doit rester aligné avec la whitelist SQL de la proc FindDocumenStore.
     * Retourne undefined si aucune colonne triée (l'API applique alors son tri par défaut : DateCreation desc).
     */
    private buildSortBy(): string | undefined {
        if (!this.lastSortColomn) {
            return undefined;
        }
        const columnToProperty: { [key: string]: string } = {
            name: 'FileName',
            type: 'TypePieceJointe',
            libelle: 'Libelle',
            commentaire: 'Commentaire',
            createdAt: 'DateCreation',
            size: 'Size',
            relatedTo: 'DocumentType'
        };
        const property = columnToProperty[this.lastSortColomn];
        if (!property) {
            return undefined;
        }
        return `${property} ${this.isAsc ? 'asc' : 'desc'}`;
    }

    compare(a: number | string, b: number | string, isAsc: boolean): number {
        return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    private putFormArrayInOtherFormArray(formArray1: UntypedFormArray, formArray2: UntypedFormArray): void {
        for (let i = 0; i < formArray1.length; i++) {
            this.files.push(formArray1.controls[i].get('file')?.value);

            formArray2?.push(
                this.fb.group({
                    file: [formArray1.controls[i].get('file')?.value, [Validators.required]],
                    typePieceJointeId: [formArray1.controls[i].get('typePieceJointeId').value, [Validators.required]],
                    libelle: [formArray1.controls[i].get('libelle')?.value],
                    commentaire: [formArray1.controls[i].get('commentaire')?.value]
                })
            );
        }
    }

    /**
     * Ouvert le popin scan document
     */
    openPopupScanDocument() {
        const config: IConfirmModalConfig = {
            confirmBtnText: 'Actions.Ok',
            canClose: false,
            canConfirm: true,
            textsKey: 'Scanner',
            bodyParams: null
        };

        const modalRef: ModalRef = this.modalService.show(ScannerComponent);
        const modal: ScannerComponent = modalRef.content;
        modal.config = config;
        modal.scannerFileName = this.scannerFileName;
    }

    /**
     * Visualiser le document numérisé
     * @param blob
     */
    viewScannedDocument(blob: Blob) {
        const downloadLink = document.createElement('a');
        const blobUrl = URL.createObjectURL(blob); // Convert blob to URL

        downloadLink.href = blobUrl;
        downloadLink.download = 'scanned-document.pdf'; // Or .jpg based on the format of your image
        downloadLink.click();
        URL.revokeObjectURL(blobUrl); // Clean up
    }

    ngOnDestroy(): void {
        this.documentGedService.initValues();
        this.documentGedService.resetDocuments();

        this.destroySubscriptions$.next(true);
        this.destroySubscriptions$.complete();
    }

    changePage(newPage: number): void {
        this.currentPage = newPage;
        this.loadDocuments();
    }

    changePageSize(newSize: number): void {
        this.currentPageSize = newSize;
        this.currentPage = 1; // Reset to first page on page size change
        this.loadDocuments();
    }

    /**
     * Recharge la liste paginée depuis l'API en respectant le tri courant.
     * Le serveur renvoie la page déjà triée : aucun tri client n'est appliqué ici.
     */
    private loadDocuments(): void {
        const docObservable = this.getDocumentService();
        if (!docObservable) {
            return;
        }
        this.isLoadingFiles = true;
        docObservable
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe({
                next: res => {
                    this.hasMoreItems = res.hasMoreItems;
                    this.skipCount = res.skipCount;
                    this.gedDocs = res?.documents?.filter(x => this.filesToDeleteIds.indexOf(x?.id) === -1);
                    this.affaireGedDocs = res?.documents?.filter(x => this.filesToDeleteIds.indexOf(x?.id) === -1);
                    if (this.isConsultDocMode && this.isBack && this.gedDocs?.length == 1) {
                        this.getUrlFile(this.gedDocs[0]);
                    }
                    this.isLoadingFiles = false;
                },
                error: () => {
                    this.isLoadingFiles = false;
                }
            });
    }

    /**
     * Afficher une modal de notification
     * @param  textsKey
     * @param bodyParams
     */
    showModal(textsKey: string, bodyParams?: any): void {
        const config: IConfirmModalConfig = {
            confirmBtnText: 'Actions.Ok',
            canClose: false,
            textsKey,
            bodyParams
        };
        this.confirmationService.openConfirmationModal(config);
    }

    private getDocumentService(): Observable<PageDocument> {
        const sortBy = this.buildSortBy();
        let docObservable: Observable<PageDocument>;
        if (this.operationId) {
            docObservable = this.documentGedApiService.getOperationDocuments(this.operationId, this.currentPageSize, this.currentPage, sortBy);
        }
        if (this.compteId && !this.structureId) {
            docObservable = this.documentGedApiService.getCompteDocuments(this.compteId, this.currentPageSize, this.currentPage, false, sortBy);
        }
        if (this.compteId && this.structureId && this.signatureId) {
            docObservable = this.documentGedApiService.getRecueilSignatureDocuments(
                this.signatureId,
                this.currentPageSize,
                this.currentPage,
                false,
                sortBy
            );
        }
        if (this.evenementId) {
            docObservable = this.documentGedApiService.getEventDocuments(this.evenementId, this.currentPageSize, this.currentPage, sortBy);
        }
        if (this.courrierId) {
            docObservable = this.documentGedApiService.getCourrierDocuments(this.courrierId, this.currentPageSize, this.currentPage, sortBy);
        }
        if (this.delegationBancaireId) {
            docObservable = this.documentGedApiService.getDelegationBancaireDocuments(this.delegationBancaireId, this.currentPageSize, this.currentPage, sortBy);
        }
        // NB: getAffaireDocumentsByType cible l'API Alfresco transitoire (non triée) -> pas de sortBy.
        if (this.affaireId) {
            docObservable = this.documentGedApiService.getAffaireDocumentsByType(
                this.affaireId,
                DocumentOrigin.avocat,
                this.currentPageSize,
                this.currentPage
            );
        }
        if (this.pageAffaireId) {
            docObservable = this.documentGedApiService.getAffaireDocuments(this.pageAffaireId, this.currentPageSize, this.currentPage, false, sortBy);
        }

        return docObservable;
    }
}
